import type { Except } from 'type-fest';
import type { Feature } from '@public-api-contracts/models/chat/ChatMessage';
import type { MainFeatureItem } from '../../../models/features/main/MainFeatureItem';
import type { ChatSidebarFeatureItem } from '../../../models/features/sidebar/ChatSidebarFeatureItem';
import type { AppDispatch, RootState } from '../../../store/store';
import type { FeatureHandler } from '../FeatureHandler';
import type { Product } from '../../../models/Product';
import type { ChatMessage } from '../../../models/ChatMessage';
import { getNewFeatureUuid } from '../../FeatureService';
import { apiClient } from '../../ApiClient';
import { getCurrentSession } from '../../CustomerSessionService';
import { translate } from '../../TranslationService';
import type { ChatFeatureOptions } from '../../../api/models/FeatureOptions';
import {
    chatContext,
    type ChatContext
} from '@public-api-contracts/models/chat/ChatContext';

export const chatSidebarFeatureHandler = {
    async startFeatureItem(
        dispatch: AppDispatch,
        state: RootState,
        featureOptions: ChatFeatureOptions
    ): Promise<void> {
        const sidebarFeatureItem = state.featureState.sidebarFeatureItem;
        if (
            sidebarFeatureItem?.featureType === 'chat' &&
            sidebarFeatureItem.chatType === 'general' &&
            featureOptions.chatType === 'general'
        ) {
            return Promise.resolve();
        }
        const { id } = await apiClient.chat.createChat({
            body: {
                sessionId: (await getCurrentSession()).id,
                chatType: featureOptions.chatType,
                context: mapMainFeatureItemToContext(
                    state.featureState.mainFeatureItem!
                )
            }
        });
        dispatch.featureState.setSidebarFeatureItem({
            chatId: id,
            featureUuid: getNewFeatureUuid('chat'),
            featureType: 'chat',
            chatMessages: [],
            loadingStatus: 'idle',
            chatType: featureOptions.chatType
        });
        return Promise.resolve();
    },
    async updateChatContext(
        state: RootState
    ): Promise<ChatContext | undefined> {
        const { sidebarFeatureItem } = state.featureState;
        if (sidebarFeatureItem?.featureType !== 'chat') {
            return;
        }
        if (!state.featureState.mainFeatureItem) {
            return;
        }
        const newContext = await apiClient.chat.updateChatContext({
            body: {
                chatId: sidebarFeatureItem.chatId,
                context: mapMainFeatureItemToContext(
                    state.featureState.mainFeatureItem
                )
            }
        });
        return newContext;
    },
    appendNewChatMessage(
        dispatch: AppDispatch,
        state: RootState,
        chatMessage: Except<ChatMessage, 'timestamp' | 'uuid'>
    ): ChatMessage | null {
        const { sidebarFeatureItem } = state.featureState;
        if (sidebarFeatureItem?.featureType !== 'chat') {
            return null;
        }
        const newChatMessage: ChatMessage = {
            timestamp: new Date().getTime(),
            uuid:
                typeof crypto.randomUUID !== 'undefined'
                    ? crypto.randomUUID()
                    : uuidv4(),
            ...chatMessage
        };
        dispatch.featureState.updateSidebarFeatureItem({
            ...sidebarFeatureItem,
            chatMessages: [...sidebarFeatureItem.chatMessages, newChatMessage]
        });
        if (chatMessage.role === 'user') {
            void dispatch.featureState.sendMessage({
                handleLoading: true
            });
        }
        return newChatMessage;
    },
    patchExistingChatMessage(
        dispatch: AppDispatch,
        state: RootState,
        partialChatMessage: Partial<ChatMessage> & { uuid: string }
    ): { index: number; chatMessage: ChatMessage | null } {
        const { sidebarFeatureItem } = state.featureState;
        if (sidebarFeatureItem?.featureType !== 'chat') {
            return { index: -1, chatMessage: null };
        }
        const originalChatMessageIndex =
            sidebarFeatureItem.chatMessages.findIndex(
                msg => msg.uuid === partialChatMessage.uuid
            );
        if (originalChatMessageIndex === -1) {
            return { index: -1, chatMessage: null };
        }
        const originalChatMessage = sidebarFeatureItem.chatMessages[
            originalChatMessageIndex
        ] as ChatMessage;
        const updatedChatMessage: ChatMessage = {
            ...originalChatMessage,
            ...partialChatMessage
        } as ChatMessage;
        const updatedChatMessages = [...sidebarFeatureItem.chatMessages];
        updatedChatMessages[originalChatMessageIndex] = updatedChatMessage;
        dispatch.featureState.updateSidebarFeatureItem({
            ...sidebarFeatureItem,
            chatMessages: updatedChatMessages
        });
        return {
            index: originalChatMessageIndex,
            chatMessage: updatedChatMessage
        };
    },
    removeSearchByImageMessages(dispatch: AppDispatch, state: RootState): void {
        const { sidebarFeatureItem } = state.featureState;
        if (sidebarFeatureItem?.featureType !== 'chat') {
            return;
        }
        dispatch.featureState.updateSidebarFeatureItem({
            ...sidebarFeatureItem,
            chatMessages: sidebarFeatureItem.chatMessages.filter(
                msg => msg.content.contentType !== 'product-search-by-image'
            )
        });
    },
    appendMainFeatureItemChatMessage(
        dispatch: AppDispatch,
        state: RootState,
        featureItem: MainFeatureItem
    ): void {
        const { sidebarFeatureItem } = state.featureState;
        if (sidebarFeatureItem?.featureType !== 'chat') {
            return;
        }
        const feature = getFeatureFromMainFeatureItem(featureItem);
        if (!feature) {
            return;
        }
        const newChatMessage: Except<ChatMessage, 'timestamp' | 'uuid'> = {
            role: 'assistant',
            content: {
                contentType: 'feature',
                startedBy: 'user-from-ui',
                status: 'success',
                feature
            }
        };
        dispatch.featureState.appendNewChatMessage(newChatMessage);
    },
    async sendMessage(
        dispatch: AppDispatch,
        state: RootState,
        options: { handleLoading?: boolean }
    ): Promise<void> {
        const { sidebarFeatureItem, mainFeatureItem } = state.featureState;
        if (
            sidebarFeatureItem?.featureType !== 'chat' ||
            !mainFeatureItem ||
            sidebarFeatureItem.chatMessages.length === 0
        ) {
            return;
        }
        const newSidebarFeatureItem: ChatSidebarFeatureItem = {
            ...sidebarFeatureItem
        };
        if (options.handleLoading) {
            dispatch.featureState.updateSidebarFeatureItem({
                ...newSidebarFeatureItem,
                loadingStatus: 'loading'
            });
        }
        let resendAutomatically: boolean = true;
        try {
            const sendingMessage =
                sidebarFeatureItem.chatMessages[
                    sidebarFeatureItem.chatMessages.length - 1
                ];
            const result = await apiClient.chat.sendMessage({
                body: {
                    chatId: sidebarFeatureItem.chatId,
                    sessionId: (await getCurrentSession()).id,
                    message: sendingMessage
                }
            });
            if (result.assistantChatMessages.length === 0) {
                resendAutomatically = false;
            }
            const newChatMessages = [...sidebarFeatureItem.chatMessages];
            let featureItemToRestore: Feature | null = null;
            for (const chatMessage of result.assistantChatMessages) {
                const replacingMessageIndex = newChatMessages.findIndex(
                    msg => msg.uuid === chatMessage.uuid
                );
                if (replacingMessageIndex !== -1) {
                    newChatMessages[replacingMessageIndex] = {
                        ...newChatMessages[replacingMessageIndex],
                        ...chatMessage
                    };
                } else {
                    newChatMessages.push(chatMessage);
                }
                const content = chatMessage.content;
                if (content.contentType === 'apply-filters') {
                    await dispatch.featureState.applyProductSearchFilters({
                        appliedCategoryFilters: content.categoryFilters,
                        appliedFieldFilters: content.fieldFilters
                    });
                }
                if (
                    content.contentType === 'feature' &&
                    content.status === 'success' &&
                    isFeatureRelevantToBeRestored(content.feature)
                ) {
                    featureItemToRestore = content.feature;
                }
            }
            const lastMessage = newChatMessages[newChatMessages.length - 1];
            if (
                resendAutomatically === true &&
                lastMessage.role === 'assistant' &&
                lastMessage.content.contentType !== 'feature'
            ) {
                resendAutomatically = false;
            }
            dispatch.featureState.updateSidebarFeatureItem({
                ...newSidebarFeatureItem,
                loadingStatus: 'success',
                chatMessages: newChatMessages
            });
            if (featureItemToRestore) {
                dispatch.featureState.restoreFeatureItem(featureItemToRestore);
            }
        } catch {
            dispatch.featureState.updateSidebarFeatureItem({
                ...newSidebarFeatureItem,
                loadingStatus: 'error'
            });
            resendAutomatically = false;
        }
        await dispatch.featureState.updateContext();
        if (resendAutomatically) {
            void dispatch.featureState.sendMessage({
                handleLoading: false
            });
        }
    },
    appendAddToCartErrorMessage(dispatch: AppDispatch, state: RootState): void {
        dispatch.featureState.appendNewChatMessage({
            role: 'assistant',
            content: {
                contentType: 'markdown',
                markdown: translate('PRODUCT_CARD_ADD_ERROR')
            }
        });
    },
    appendProductAddedToCartMessage(
        dispatch: AppDispatch,
        state: RootState,
        data: { product: Product; variantId: string }
    ): void {
        const { product, variantId } = data;
        dispatch.featureState.appendNewChatMessage({
            role: 'assistant',
            content: {
                contentType: 'add-to-cart',
                product,
                variantId
            }
        });
    },
    appendProductSearchByImageMessage(
        dispatch: AppDispatch,
        state: RootState
    ): void {
        dispatch.featureState.appendNewChatMessage({
            role: 'assistant',
            content: {
                contentType: 'product-search-by-image'
            }
        });
    }
} as const satisfies FeatureHandler<ChatSidebarFeatureItem>;

function getFeatureFromMainFeatureItem(
    featureItem: MainFeatureItem
): Feature | null {
    switch (featureItem.featureType) {
        case 'product-comparison':
            return {
                featureType: 'product-comparison',
                featureUuid: featureItem.featureUuid,
                request: {
                    productIds: featureItem.comparingProductIds
                }
            };
        case 'product-search-new':
            return {
                featureType: 'product-search-new',
                featureUuid: featureItem.featureUuid,
                request: {
                    pageSize: 24,
                    sortBy: featureItem.appliedSortBy
                }
            };
        case 'product-search-similar':
            return {
                featureType: 'product-search-similar',
                featureUuid: featureItem.featureUuid,
                request: {
                    productIds: featureItem.baseProductsIds,
                    pageSize: 24,
                    sortBy: featureItem.appliedSortBy
                }
            };
        case 'product-search-combinations':
            return {
                featureType: 'product-search-combinations',
                featureUuid: featureItem.featureUuid,
                request: {
                    productIds: featureItem.baseProductsIds,
                    maxGroups: 4,
                    maxGroupSize: 8,
                    sortBy: featureItem.appliedSortBy
                }
            };
        case 'product-search-combinations-by-image':
            return {
                featureType: 'product-search-combinations-by-image',
                featureUuid: featureItem.featureUuid,
                request: {
                    imageId: featureItem.imageId,
                    imageUrl: featureItem.imageUrl,
                    maxGroups: 4,
                    maxGroupSize: 8,
                    sortBy: featureItem.appliedSortBy
                }
            };
    }
    return null;
}

function uuidv4() {
    return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, c =>
        (
            +c ^
            (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))
        ).toString(16)
    );
}

function mapMainFeatureItemToContext(
    featureItem: MainFeatureItem
): ChatContext {
    const result = chatContext.safeParse(featureItem);
    if (!result.success) {
        throw new Error(`Failed to parse feature item: ${result.error}`);
    }
    return result.data;
}

function isFeatureRelevantToBeRestored(feature: Feature | null): boolean {
    if (!feature || !feature.result) {
        return false;
    }
    switch (feature.featureType) {
        case 'product-detail':
        case 'product-comparison':
        case 'product-mix-and-match':
            return true;
        case 'product-search-new':
        case 'product-search-similar':
        case 'product-search-by-text':
        case 'product-search-similar-by-image':
        case 'product-search-complementary-category':
            return feature.result.products.length > 0;
        case 'product-search-combinations':
        case 'product-search-combinations-by-image':
            return feature.result.groups.length > 0;
        default:
            return false;
    }
}
